import { useCallback, useEffect, useState } from 'react';
import {
  ArrowTrendingUpIcon,
  BuildingOffice2Icon,
  CurrencyRupeeIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import api, { getErrorMessage } from '@/services/api';
import { EmptyState, LoadingSpinner, StatsCard, StatsGrid } from '@/components/common';
import { SingleSeriesBarChart } from '@/components/charts/SingleSeriesBarChart';
import { TrendLineChart } from '@/components/charts/TrendLineChart';
import { formatCurrency, formatCurrencyCompact } from '@/utils/format';
import { TargetVsAchievementWidget } from '@/pages/targets/TargetVsAchievementWidget';
import type { AchievementResponse } from '@/services/targets';
import { POLICY_COUNT_NOTE, REVENUE_METHODOLOGY } from './reportDates';
import { formatNumber } from '@/utils';

/**
 * Board #92 (Broker Performance Analytics) and the #90 dashboard summary.
 *
 * Deliberately sparse where the data is sparse. Across the whole platform
 * there are 12 leads (4 won) belonging to 3 brokers, so most country slices
 * have no lead funnel at all — this page renders "No data" for those widgets
 * instead of a 0% conversion rate, which would be a claim the data does not
 * support.
 *
 * Board #263 replaced the target-vs-achievement placeholder. Broker-level
 * monthly premium targets now exist (`apps.targets`), so the widget reads
 * GET /targets/achievement/ — one row per broker in the country, achievement
 * derived from total premium on issued policies bucketed on the issue date
 * (falling back to inception).
 */

interface SeriesRow {
  period: string;
  policies: number;
  premium: string;
}

interface TopBroker {
  broker_id: string;
  name: string;
  code: string;
  policies: number;
  premium: string;
}

interface Performance {
  period: string;
  monthly: SeriesRow[];
  yearly: SeriesRow[];
  conversion: {
    leads: number;
    won: number;
    /** null — not 0 — when there are no leads to divide by. */
    rate: number | null;
    brokers_with_leads: number;
  };
  top_brokers: TopBroker[];
  has_data: boolean;
}

interface Summary {
  period: string;
  total_brokers: number;
  active_brokers: number;
  inactive_brokers: number;
  /** null — not 0 — when there are no brokers to take a percentage of. */
  active_percentage: number | null;
  /** #388: the dashboard's "Total Policies" count (any status, not archived). */
  total_policies: number;
  /** Sold policies only — the set total_premium is summed over. */
  sold_policies: number;
  total_premium: string;
  total_commission: string;
  platform_revenue: string;
  total_posps: number;
  active_posps: number;
  total_employees: number;
  generated_at: string;
}

const BASE = '/brokers/country-reports';

export function BrokerAnalyticsPage() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [performance, setPerformance] = useState<Performance | null>(null);
  const [achievement, setAchievement] = useState<AchievementResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, p, a] = await Promise.all([
        api.get<Summary>(`${BASE}/summary/`),
        api.get<Performance>(`${BASE}/performance/`),
        api.get<AchievementResponse>('/targets/achievement/'),
      ]);
      setSummary(s.data);
      setPerformance(p.data);
      setAchievement(a.data);
    } catch (err) {
      // Both tiles and charts stay unrendered on failure. A dashboard that
      // paints zeros when its query failed is indistinguishable from a
      // platform with no business — the one thing this page must never do.
      setSummary(null);
      setPerformance(null);
      setAchievement(null);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Broker Performance</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Sales, conversion and ranking across every broker in your country.
        </p>
      </div>

      {error && (
        <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <p className="font-semibold">Analytics could not be loaded.</p>
          <p className="mt-1">{error}</p>
        </div>
      )}

      {loading && !error && (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {!loading && !error && summary && (
        <StatsGrid>
          <StatsCard title="Total Brokers" value={summary.total_brokers} icon={BuildingOffice2Icon} />
          <StatsCard
            title="Active Brokers"
            value={
              summary.active_percentage === null
                ? 'No brokers'
                : `${summary.active_brokers} · ${summary.active_percentage}%`
            }
            subtitle={
              summary.active_percentage === null
                ? 'No brokers in this country yet'
                : `${summary.inactive_brokers} not active`
            }
            icon={ArrowTrendingUpIcon}
          />
          <StatsCard
            title="Premium (Revenue)"
            value={formatCurrencyCompact(summary.total_premium)}
            valueTitle={formatCurrency(summary.total_premium)}
            subtitle={`${formatNumber(summary.sold_policies)} sold · ${formatNumber(summary.total_policies)} total policies`}
            icon={CurrencyRupeeIcon}
          />
          <StatsCard
            title="POSPs"
            value={summary.total_posps}
            subtitle={`${formatNumber(summary.active_posps)} active · ${formatNumber(summary.total_employees)} employees`}
            icon={UserGroupIcon}
          />
        </StatsGrid>
      )}

      {!loading && !error && summary && (
        <div className="space-y-1 text-xs text-gray-500 dark:text-gray-400" data-testid="revenue-methodology">
          <p>{REVENUE_METHODOLOGY}</p>
          <p>{POLICY_COUNT_NOTE}</p>
        </div>
      )}

      {!loading && !error && summary && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Panel title="Commission booked">
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {formatCurrency(summary.total_commission)}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              Of which platform revenue: {formatCurrency(summary.platform_revenue)}
            </p>
          </Panel>
          <Panel title="Lead → policy conversion">
            {performance && performance.conversion.leads === 0 ? (
              <EmptyState
                title="No data"
                description="No leads have been recorded for brokers in your country, so there is no conversion rate to show."
              />
            ) : (
              performance && (
                <>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {performance.conversion.rate}%
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    {formatNumber(performance.conversion.won)} won of{' '}
                    {formatNumber(performance.conversion.leads)} leads, across{' '}
                    {formatNumber(performance.conversion.brokers_with_leads)} broker(s)
                  </p>
                </>
              )
            )}
          </Panel>
        </div>
      )}

      {!loading && !error && performance && (
        <>
          <Panel title="Monthly sales">
            <TrendLineChart
              data={performance.monthly.map((r) => ({
                period: r.period,
                policies: r.policies,
                premium: Number(r.premium),
              }))}
              xKey="period"
              series={[
                { dataKey: 'policies', label: 'Policies' },
                { dataKey: 'premium', label: 'Premium' },
              ]}
              emptyMessage="No sold policies in this period."
            />
          </Panel>

          <Panel title="Yearly sales">
            <SingleSeriesBarChart
              data={performance.yearly.map((r) => ({ label: r.period, value: Number(r.premium) }))}
              valueFormatter={(v) => formatCurrency(v)}
              emptyMessage="No sold policies in this period."
            />
          </Panel>

          <Panel title="Top-performing brokers">
            {performance.top_brokers.length === 0 ? (
              <EmptyState
                title="No data"
                description="No broker in your country has sold a policy in this period."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead>
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">#</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">Broker</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">Policies</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">Premium</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {performance.top_brokers.map((b, i) => (
                      <tr key={b.broker_id}>
                        <td className="px-3 py-2 text-sm text-gray-500">{i + 1}</td>
                        <td className="px-3 py-2 text-sm text-gray-800 dark:text-gray-200">
                          {b.name} <span className="text-gray-400">({b.code})</span>
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-800 dark:text-gray-200">
                          {formatNumber(b.policies)}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-800 dark:text-gray-200">
                          {formatCurrency(b.premium)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <Panel
            title={
              achievement
                ? `Target vs achievement (${achievement.from} to ${achievement.to})`
                : 'Target vs achievement'
            }
          >
            <TargetVsAchievementWidget
              rows={achievement?.rows ?? []}
              top={achievement?.top ?? []}
              bottom={achievement?.bottom ?? []}
              entityLabel="Broker"
              emptyMessage="No brokers in your country yet, so there is nothing to measure."
            />
          </Panel>
        </>
      )}
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <h2 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-200">{title}</h2>
      {children}
    </section>
  );
}

export default BrokerAnalyticsPage;
